/**
 * The New Transition modal, from the Bridge Figma file's Happy Path:
 * - frame 2 "Documents processing" (263:1284) while the dropped files are scanned,
 *   shown as a progress panel (each file waiting, being read, done or failed);
 * - frame 3 "Documents uploaded-successfully" (256:3617), laid out like the
 *   Transition Context modal: the recommended documents (TRANSIT_CONTEXT_SLOTS),
 *   each with the files the scan sorted into it or "Missing documentation" and
 *   Upload (a file uploaded there takes the slot's type at Start), then other
 *   documentation;
 * - frame 4 "General information and additional context" (258:3819): the
 *   transition's name and any added detail, then Start.
 *
 * Each file goes to POST /documents/scan (a few at a time), which reads it and
 * names its type, vendor and product without storing anything; the browser
 * keeps the files. Start resolves the vendor and product the scans agree on
 * (reusing the org's records, creating what's new), creates the transition,
 * uploads the files and the added detail, and opens the workspace; the same
 * progress panel shows each step and upload as it goes.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Circle, Download, Info, Loader2, XCircle } from 'lucide-react'
import { transitApi } from '../../services/transit'
import type { DocumentScan, DocumentType, Product, Vendor } from '../../types/transit'
import { TRANSIT_CHANGED } from './status'
import SvgIcon from './SvgIcon'
import FileDropzone, { matchesAccept, rejectedMessage } from '../ui/FileDropzone'
import { TRANSIT_ACCEPT, TRANSIT_CONTEXT_SLOTS } from '../../constants'
import { cn } from '../../lib/utils'

export interface ScannedFile {
  id: number
  file: File
  /** queued: waiting for a free request; scanning: being read. */
  status: 'queued' | 'scanning' | 'done' | 'failed'
  scan?: DocumentScan
  error?: string
  /** Added through a recommended document's Upload: it is that document, and
   *  takes the slot's type once uploaded, whatever the scan says. */
  slot?: number
}

/** The recommended document (TRANSIT_CONTEXT_SLOTS) a file is shown under:
 *  the one it was added to, else the one its scanned type belongs to; -1 for
 *  other documentation (including files still being read). */
const slotOf = (it: ScannedFile) =>
  it.slot ?? (it.scan?.document_type ? TRANSIT_CONTEXT_SLOTS.findIndex((s) => s.types.includes(it.scan!.document_type!)) : -1)

/** Requests in flight at once: the API takes one file per request. */
const AT_ONCE = 3

/** Runs task on every item, AT_ONCE at a time. */
async function inPool<T>(items: T[], task: (item: T) => Promise<void>) {
  const queue = [...items]
  const worker = async () => {
    for (let it = queue.shift(); it !== undefined; it = queue.shift()) await task(it)
  }
  await Promise.all(Array.from({ length: Math.min(AT_ONCE, queue.length) }, worker))
}

const key = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ')

interface Choice<T> {
  name: string
  match: T | null
}

/** The name most scans gave (an existing record counts by its id), or null. */
function consensus<T extends { id: string; name: string }>(found: ({ name: string; match: T | null } | null | undefined)[]): Choice<T> | null {
  const tally = new Map<string, { pick: Choice<T>; n: number }>()
  for (const f of found) {
    if (!f) continue
    const k = f.match ? `id:${f.match.id}` : `name:${key(f.name)}`
    const t = tally.get(k) ?? { pick: { name: f.match?.name ?? f.name, match: f.match }, n: 0 }
    t.n++
    tally.set(k, t)
  }
  let best: { pick: Choice<T>; n: number } | null = null
  for (const t of tally.values()) {
    if (!best || t.n > best.n || (t.n === best.n && t.pick.match && !best.pick.match)) best = t
  }
  return best?.pick ?? null
}

function suggest(scans: DocumentScan[]) {
  const vendor = consensus<Vendor>(scans.map((s) => s.vendor))
  const sameVendor = (s: DocumentScan) =>
    !!s.vendor && !!vendor && (vendor.match ? s.vendor.match?.id === vendor.match.id : key(s.vendor.name) === key(vendor.name))
  const product = consensus<Product>(scans.filter(sameVendor).map((s) => s.product))
  return { vendor, product }
}

/** The page indicator: done steps a dot, the current one a bar, later ones a pale dot. */
function Steps({ at }: { at: 'documents' | 'details' }) {
  return at === 'documents' ? (
    <div className="flex items-center gap-2" aria-label="Step 1 of 2">
      <div className="h-[6px] w-[42px] rounded-full bg-primary" />
      <img src="/transit/step-dot.svg" alt="" aria-hidden width={6} height={6} />
    </div>
  ) : (
    <div className="flex items-center gap-2" aria-label="Step 2 of 2">
      <img src="/transit/step-done.svg" alt="" aria-hidden width={6} height={6} />
      <div className="h-[6px] w-[42px] rounded-full bg-primary" />
    </div>
  )
}

type RowState = 'waiting' | 'working' | 'done' | 'failed'

/**
 * Work in progress, made plain: a title with the count done, and every item
 * with its own state. Used while the dropped files are read and while Start
 * creates and uploads.
 */
function Progress({ title, unit, rows, note }: {
  title: string
  /** "read", "done": the count reads "2 of 5 read". */
  unit: string
  rows: Array<{ key: string | number; name: string; state: RowState; detail?: string }>
  note: string
}) {
  const finished = rows.filter((r) => r.state === 'done' || r.state === 'failed').length
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-4 rounded-[12px] bg-muted p-4">
      <div className="flex items-center gap-3">
        <Loader2 className="h-5 w-5 shrink-0 animate-spin text-brand" aria-hidden />
        <p className="flex-1 text-base font-medium text-foreground">{title}</p>
        <p className="shrink-0 text-sm tabular-nums text-tertiary">{finished} of {rows.length} {unit}</p>
      </div>
      <ul className="flex flex-col gap-2">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-3 text-sm">
            {r.state === 'working' ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand" aria-hidden />
              : r.state === 'done' ? <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden />
                : r.state === 'failed' ? <XCircle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
                  : <Circle className="h-4 w-4 shrink-0 text-tertiary" aria-hidden />}
            <span className={cn('min-w-0 flex-1 truncate', r.state === 'waiting' ? 'text-tertiary' : 'text-foreground')} title={r.name}>{r.name}</span>
            {r.detail && <span className={cn('shrink-0 text-xs', r.state === 'failed' ? 'text-destructive' : 'text-tertiary')}>{r.detail}</span>}
          </li>
        ))}
      </ul>
      <p className="text-sm text-tertiary">{note}</p>
    </div>
  )
}

// The modal's middle: it scrolls between the fixed header and the buttons, so
// the card stays inside the viewport and Continue / Start stay in view. The
// side padding keeps focus rings and the scrollbar clear of the edge.
const scrollClass = 'scroll-bridge -ml-1 -mr-4 flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto pl-1 pr-4'

const fieldClass =
  'w-full border border-input bg-card p-4 text-foreground placeholder:text-tertiary focus:outline-none focus:ring-1 focus:ring-ring'

interface Props {
  files: File[]
  onClose: () => void
}

export default function NewTransitionModal({ files, onClose }: Props) {
  const navigate = useNavigate()
  const nextId = useRef(0)
  const open = useRef(true)
  const [items, setItems] = useState<ScannedFile[]>([])
  const [reviewing, setReviewing] = useState(false)
  const [types, setTypes] = useState<Record<string, string>>({})
  const replaceInput = useRef<HTMLInputElement>(null)
  const replacing = useRef<number | null>(null)
  // A recommended document's Upload: the picker, and which slot it fills.
  const slotInput = useRef<HTMLInputElement>(null)
  const slotTarget = useRef<number>(-1)
  // Files the dropzone or Replace turned away, by name.
  const [rejected, setRejected] = useState<string | null>(null)

  const [step, setStep] = useState<'documents' | 'details'>('documents')
  const [name, setName] = useState('')
  const [details, setDetails] = useState('')
  const [vendorName, setVendorName] = useState('')
  const [productName, setProductName] = useState('')
  const [found, setFound] = useState<{ vendor: Choice<Vendor> | null; product: Choice<Product> | null }>({ vendor: null, product: null })
  const [starting, setStarting] = useState(false)
  const startingRef = useRef(false)
  startingRef.current = starting
  const [startError, setStartError] = useState<string | null>(null)
  // Start's progress: creating the transition, then each upload, then the details.
  const [phase, setPhase] = useState<'creating' | 'uploading' | 'finishing'>('creating')
  const [uploads, setUploads] = useState<Record<number, RowState>>({})

  const update = (id: number, patch: Partial<ScannedFile>) =>
    open.current && setItems((list) => list.map((it) => (it.id === id ? { ...it, ...patch } : it)))

  const scan = (batch: ScannedFile[]) =>
    inPool(batch, async (it) => {
      update(it.id, { status: 'scanning' })
      try {
        update(it.id, { status: 'done', scan: await transitApi.scanDocument(it.file) })
      } catch (e: any) {
        update(it.id, { status: 'failed', error: e?.message || 'Could not read this file' })
      }
    })

  const add = (picked: File[], slot?: number) => {
    const batch = picked.map((file) => ({ id: nextId.current++, file, status: 'queued' as const, slot }))
    setItems((list) => [...list, ...batch])
    return scan(batch)
  }

  useEffect(() => {
    open.current = true
    transitApi
      .listDocumentTypes()
      .then((ts: DocumentType[]) => open.current && setTypes(Object.fromEntries(ts.map((t) => [t.key, t.name]))))
      .catch(() => {})
    if (nextId.current === 0) add(files).then(() => open.current && setReviewing(true)) // once, even if remounted
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !startingRef.current && onClose()
    document.addEventListener('keydown', onKey)
    return () => {
      open.current = false
      document.removeEventListener('keydown', onKey)
    }
    // Scans the files the modal opened with, once.
  }, [])

  const replace = (picked: File[]) => {
    const id = replacing.current
    replacing.current = null
    if (id === null || picked.length === 0) return
    if (!matchesAccept(picked[0], TRANSIT_ACCEPT)) {
      setRejected(rejectedMessage([picked[0]]))
      return
    }
    setRejected(null)
    const it = { id, file: picked[0], status: 'queued' as const }
    update(id, { ...it, scan: undefined, error: undefined })
    scan([it])
  }

  const scanning = items.some((it) => it.status === 'queued' || it.status === 'scanning')
  const ready = items.filter((it) => it.status === 'done')
  // A required document (the vendor questionnaire) holds Continue until a
  // read file sits in its slot.
  const missingRequired = TRANSIT_CONTEXT_SLOTS.filter((s, i) => s.required && !ready.some((it) => slotOf(it) === i))

  const toDetails = () => {
    const s = suggest(ready.flatMap((it) => (it.scan ? [it.scan] : [])))
    setFound(s)
    // Keep what the user typed if they come back; otherwise fill from the scans.
    setVendorName((v) => v || s.vendor?.name || '')
    setProductName((p) => p || s.product?.name || '')
    setName((n) => n || s.product?.name || '')
    setStep('details')
  }

  /** The vendor and product to start under: the scans' match while the name is unchanged, else the org's record of that name, else a new one. */
  const resolve = async () => {
    const vName = vendorName.trim()
    let vendor =
      found.vendor?.match && key(found.vendor.match.name) === key(vName)
        ? found.vendor.match
        : (await transitApi.listVendors()).find((v) => key(v.name) === key(vName))
    vendor ??= await transitApi.createVendor(vName)
    const pName = productName.trim()
    let product =
      found.product?.match && found.product.match.vendor_id === vendor.id && key(found.product.match.name) === key(pName)
        ? found.product.match
        : (await transitApi.listProducts(vendor.id)).find((p) => key(p.name) === key(pName))
    product ??= await transitApi.createProduct(vendor.id, pName)
    return product
  }

  const start = async () => {
    setStarting(true)
    setStartError(null)
    setPhase('creating')
    setUploads(Object.fromEntries(ready.map((it) => [it.id, 'waiting' as const])))
    const mark = (id: number, state: RowState) => open.current && setUploads((all) => ({ ...all, [id]: state }))
    try {
      const product = await resolve()
      const t = await transitApi.createTransition(product.id, name.trim())
      if (open.current) setPhase('uploading')
      const failed: string[] = []
      await inPool(ready, async (it) => {
        mark(it.id, 'working')
        try {
          const doc = await transitApi.uploadDocument(t.id, it.file)
          // Uploaded into a recommended document: it is that document.
          if (it.slot !== undefined) await transitApi.setDocumentType(doc.id, TRANSIT_CONTEXT_SLOTS[it.slot].types[0])
          mark(it.id, 'done')
        } catch {
          mark(it.id, 'failed')
          failed.push(it.file.name)
        }
      })
      if (details.trim()) {
        if (open.current) setPhase('finishing')
        await transitApi.addWrittenInput(t.id, 'Additional details', details.trim())
      }
      window.dispatchEvent(new Event(TRANSIT_CHANGED))
      if (failed.length) window.alert(`The transition was created, but these didn't upload; add them in the workspace:\n${failed.join('\n')}`)
      navigate(`/transit/${t.id}`)
      onClose()
    } catch (e: any) {
      if (open.current) setStartError(e?.message || 'Could not start the transition')
    } finally {
      if (open.current) setStarting(false)
    }
  }

  const missingNames = !found.vendor || !found.product
  const canStart = !!name.trim() && !!vendorName.trim() && !!productName.trim() && !starting

  // One file: what the scan found (or where reading stands), its name, and
  // Replace / Remove.
  const fileRow = (it: ScannedFile) => (
    <div key={it.id} className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        {/* The type it will have: the slot's when it was uploaded into one,
            else what the scan found. */}
        <p className={`flex items-center gap-2 text-xs ${it.status === 'failed' ? 'text-destructive' : 'text-tertiary'}`}>
          {it.status === 'queued' || it.status === 'scanning'
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin text-brand" aria-hidden /> Reading…</>
            : it.status === 'failed'
              ? it.error
              : it.slot !== undefined
                ? types[TRANSIT_CONTEXT_SLOTS[it.slot].types[0]] ?? TRANSIT_CONTEXT_SLOTS[it.slot].label
                : (it.scan?.document_type && types[it.scan.document_type]) || 'Unsorted'}
        </p>
        <p className="break-words font-mono text-xs leading-4 text-secondary-foreground">{it.file.name}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          title="Replace file"
          aria-label={`Replace ${it.file.name}`}
          disabled={it.status === 'queued' || it.status === 'scanning'}
          onClick={() => {
            replacing.current = it.id
            replaceInput.current?.click()
          }}
          className="flex size-8 items-center justify-center rounded-[6px] bg-secondary hover:bg-secondary-hover disabled:opacity-50"
        >
          <SvgIcon src="/transit/icon-replace.svg" width={16} height={16} className="text-foreground" />
        </button>
        <button
          type="button"
          title="Remove"
          aria-label={`Remove ${it.file.name}`}
          onClick={() => setItems((list) => list.filter((x) => x.id !== it.id))}
          className="flex size-8 items-center justify-center rounded-[6px] bg-secondary hover:bg-secondary-hover"
        >
          <SvgIcon src="/transit/icon-remove.svg" width={16} height={16} className="text-foreground" />
        </button>
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="New Transition">
      {/* 785 wide, like the Transition Context modal, and the window's height
          (between the CUI banners, when on) less 30px above and below, so it
          doesn't resize between steps. */}
      <div className="flex h-[calc(100dvh-60px-var(--cui-banner-top)-var(--cui-banner-bottom))] w-full max-w-[785px] flex-col gap-8 rounded-[12px] bg-card py-6 pl-[27px] pr-6">
        <div className="flex shrink-0 flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-base text-tertiary">New Transition</p>
            <button
              type="button"
              onClick={onClose}
              disabled={starting}
              className="flex h-8 min-w-16 items-center justify-center rounded-[8px] bg-secondary px-3 py-2 text-sm font-medium leading-4 text-secondary-foreground hover:bg-secondary-hover disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
          <div className="flex flex-col gap-3">
            <Steps at={step} />
            <div className="flex flex-col gap-3 text-muted-foreground">
              <p className="text-xl font-medium">{step === 'documents' ? 'Documents' : 'General information'}</p>
              <p className="text-sm">
                {step === 'documents'
                  ? 'For the best results, upload all documentation below'
                  : starting ? 'Setting up your transition and its documents'
                    : 'Name your transition and add any additional context'}
              </p>
            </div>
          </div>
        </div>

        {step === 'details' && starting ? (
          <div className={scrollClass}>
          <Progress
            title={phase === 'creating' ? 'Creating your transition' : phase === 'uploading' ? 'Uploading your documents' : 'Saving your details'}
            unit="done"
            rows={[
              { key: 'create', name: 'Create the transition', state: phase === 'creating' ? 'working' : 'done' },
              ...ready.map((it) => ({ key: it.id, name: it.file.name, state: uploads[it.id] ?? 'waiting',
                detail: uploads[it.id] === 'failed' ? 'Didn’t upload' : undefined })),
              ...(details.trim() ? [{ key: 'details', name: 'Additional details', state: (phase === 'finishing' ? 'working' : 'waiting') as RowState }] : []),
            ]}
            note="Keep this window open; the workspace opens when everything is in."
          />
          </div>
        ) : step === 'details' ? (
          <>
            <div className={scrollClass}>
            <label className="flex flex-col gap-3">
              <span className="flex gap-1 text-sm font-medium">
                <span className="text-foreground">Transition name</span>
                <span className="text-tertiary">(Required)</span>
              </span>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`${fieldClass} rounded-[12px] text-base`}
              />
            </label>

            {/* Not in the frame: shown only when the documents didn't name the vendor or product. */}
            {missingNames && (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-muted-foreground">
                  Your documents didn&rsquo;t name the {!found.vendor && !found.product ? 'vendor or product' : !found.vendor ? 'vendor' : 'product'}.
                </p>
                <div className="flex gap-2">
                  <label className="flex flex-1 flex-col gap-2 text-sm font-medium text-foreground">
                    Vendor
                    <input value={vendorName} onChange={(e) => setVendorName(e.target.value)} className={`${fieldClass} rounded-[12px] text-base font-normal`} />
                  </label>
                  <label className="flex flex-1 flex-col gap-2 text-sm font-medium text-foreground">
                    Product
                    <input value={productName} onChange={(e) => setProductName(e.target.value)} className={`${fieldClass} rounded-[12px] text-base font-normal`} />
                  </label>
                </div>
              </div>
            )}

            <label className="flex flex-col gap-3">
              <span className="flex flex-col gap-[6px] text-sm">
                <span className="font-medium text-foreground">Additional details</span>
                <span className="text-muted-foreground">Would you like to add anything about this transition?</span>
              </span>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Add anything that helps describe your program, such as goals, challenges, timelines, or what you’re looking for"
                className={`${fieldClass} h-[108px] resize-none rounded-[16px] text-sm leading-[1.5]`}
              />
            </label>

            {startError && <p className="text-sm text-destructive">{startError}</p>}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setStep('documents')}
                disabled={starting}
                className="flex h-10 min-w-20 flex-1 items-center justify-center gap-2 rounded-[8px] bg-secondary px-4 py-3 text-sm font-medium leading-4 text-secondary-foreground hover:bg-secondary-hover disabled:opacity-60"
              >
                <SvgIcon src="/transit/icon-back.svg" width={16} height={16} />
                Back to documents
              </button>
              <button
                type="button"
                onClick={start}
                disabled={!canStart}
                className="flex h-10 min-w-20 flex-1 items-center justify-center rounded-[8px] bg-primary px-4 py-3 text-sm font-medium leading-4 text-primary-foreground disabled:cursor-default disabled:opacity-60"
              >
                {starting ? 'Starting…' : 'Start'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={scrollClass}>
            {!reviewing ? (
              <Progress
                title="Reading your documents"
                unit="read"
                rows={items.map((it) => ({
                  key: it.id,
                  name: it.file.name,
                  state: it.status === 'queued' ? 'waiting' : it.status === 'scanning' ? 'working' : it.status,
                  detail: it.status === 'done'
                    ? (it.scan?.document_type && types[it.scan.document_type]) || 'Unsorted'
                    : it.status === 'failed' ? it.error : undefined,
                }))}
                note="Each one is read for its type, vendor and product. Nothing is saved until you start."
              />
            ) : (
              <>
                {/* Like the Transition Context modal: the recommended documents,
                    each with its files or "Missing documentation" and Upload,
                    then everything else. */}
                <div className="flex flex-col gap-3">
                  <p className="text-sm font-medium text-foreground">Recommended documents</p>
                  <div className="flex flex-col divide-y divide-input border-y border-input">
                    {TRANSIT_CONTEXT_SLOTS.map((slot, i) => {
                      const files = items.filter((it) => slotOf(it) === i)
                      return (
                        <div key={slot.label} className="flex flex-col gap-2 py-3">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex min-w-0 flex-col gap-2">
                              <p className="text-sm font-medium text-foreground">{slot.label}</p>
                              {files.length === 0 && <p className="font-mono text-xs leading-4 text-destructive">Missing documentation</p>}
                              {files.length === 0 && slot.template && (
                                <a href={slot.template.href} download
                                  className="flex w-fit items-center gap-1 text-xs leading-4 text-muted-foreground underline underline-offset-2 hover:text-foreground">
                                  <Download className="h-3.5 w-3.5 shrink-0" aria-hidden /> {slot.template.label}
                                </a>
                              )}
                              {/* A required document the scan sorted elsewhere: move a read
                                  file here (it takes this slot's type at Start), rather than
                                  uploading it a second time. */}
                              {files.length === 0 && slot.required && ready.length > 0 && (
                                <label className="flex items-center gap-2 text-xs leading-4 text-muted-foreground">
                                  Or use a file you added:
                                  <select value="" aria-label={`Use a file as the ${slot.label.toLowerCase()}`}
                                    onChange={(e) => {
                                      const id = Number(e.target.value)
                                      setItems((list) => list.map((x) => (x.id === id ? { ...x, slot: i } : x)))
                                    }}
                                    className="max-w-64 truncate rounded-[6px] border border-input bg-card px-2 py-1 text-xs text-foreground">
                                    <option value="" disabled>Choose a file</option>
                                    {ready.map((it) => <option key={it.id} value={it.id}>{it.file.name}</option>)}
                                  </select>
                                </label>
                              )}
                            </div>
                            {files.length === 0 && (
                              <button type="button" onClick={() => { slotTarget.current = i; slotInput.current?.click() }}
                                className="flex h-8 min-w-16 shrink-0 items-center justify-center gap-1 rounded-[6px] bg-primary px-3 text-sm font-medium leading-4 tracking-[0.56px] text-primary-foreground">
                                Upload <SvgIcon src="/transit/icon-replace.svg" width={16} height={16} />
                              </button>
                            )}
                          </div>
                          {files.map((it) => fileRow(it))}
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <p className="text-sm font-medium text-foreground">Other documentation</p>
                  {items.some((it) => slotOf(it) === -1) && (
                    <div className="flex flex-col gap-2 border-t border-input pt-3">
                      {items.filter((it) => slotOf(it) === -1).map((it) => fileRow(it))}
                    </div>
                  )}
                  {/* More files: drop them here or click to browse; each is
                      scanned like the ones the modal opened with, and lands
                      under the recommended document its type fills. */}
                  <FileDropzone
                    accept={TRANSIT_ACCEPT}
                    onFilesSelected={(picked) => { setRejected(null); add(picked) }}
                    onReject={(picked) => setRejected(rejectedMessage(picked))}
                    className="group cursor-pointer rounded-lg border border-primary p-1"
                  >
                    <div className="flex flex-col items-center gap-1 rounded-[12px] bg-background px-3 py-4 transition-colors group-hover:bg-muted group-data-[drag-active=true]:bg-muted">
                      <SvgIcon src="/transit/icon-add.svg" width={16} height={16} className="text-foreground" />
                      <p className="text-sm font-medium leading-5 text-muted-foreground">Drop more files here or click to browse</p>
                    </div>
                  </FileDropzone>
                  {rejected && <p role="alert" className="text-sm text-destructive">{rejected}</p>}
                </div>
              </>
            )}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-4">
              {reviewing && !scanning && missingRequired.length > 0 && (
                // The home page's mild alert: the attention yellow of the "Needs input" chips.
                <p role="note" className="flex items-center gap-2 rounded-md bg-status-attention/60 px-3 py-1.5 text-sm leading-5 text-status-foreground dark:bg-status-attention">
                  <Info className="h-4 w-4 shrink-0" aria-hidden />
                  Attach the {missingRequired.map((s) => s.label.toLowerCase()).join(' and ')} to continue.
                </p>
              )}
              <button
                type="button"
                disabled={!reviewing || scanning || ready.length === 0 || missingRequired.length > 0}
                onClick={toDetails}
                className="flex h-10 w-[272.5px] min-w-20 items-center justify-center rounded-[6px] bg-primary px-4 py-3 text-sm font-medium leading-4 text-primary-foreground disabled:cursor-default disabled:opacity-60"
              >
                Continue
              </button>
            </div>
          </>
        )}

        <input
          ref={slotInput}
          type="file"
          accept={TRANSIT_ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? [])
            e.target.value = ''
            const bad = picked.filter((f) => !matchesAccept(f, TRANSIT_ACCEPT))
            setRejected(bad.length ? rejectedMessage(bad) : null)
            const ok = picked.filter((f) => matchesAccept(f, TRANSIT_ACCEPT))
            if (ok.length) add(ok, slotTarget.current)
          }}
        />
        <input
          ref={replaceInput}
          type="file"
          accept={TRANSIT_ACCEPT}
          className="hidden"
          onChange={(e) => {
            replace(Array.from(e.target.files ?? []))
            e.target.value = ''
          }}
        />
      </div>
    </div>
  )
}
